import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { TOTAL_LESSONS } from "@/data/academy";
import { getLang, setLang as persistLang, makeT } from "@/lib/i18n";

const AcademyContext = createContext(null);

export function AcademyProvider({ children }) {
  const [progress, setProgress] = useState({ completed_lessons: [], quiz_scores: {} });
  const [lang, setLangState] = useState(getLang());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/academy/progress");
      setProgress(data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const completeLesson = useCallback(async (lessonId) => {
    try {
      const { data } = await api.post("/academy/lesson/complete", { lesson_id: lessonId });
      setProgress(data);
    } catch {}
  }, []);

  const submitQuiz = useCallback(async (lessonId, score, total) => {
    try {
      const { data } = await api.post("/academy/quiz/submit", { lesson_id: lessonId, score, total });
      setProgress(data);
    } catch {}
  }, []);

  const setLang = useCallback((code) => {
    persistLang(code);
    setLangState(code);
  }, []);

  const t = useMemo(() => makeT(lang), [lang]);

  const completedSet = useMemo(
    () => new Set(progress.completed_lessons || []),
    [progress.completed_lessons]
  );
  const overallPct = TOTAL_LESSONS
    ? Math.round((completedSet.size / TOTAL_LESSONS) * 100)
    : 0;

  const isDone = useCallback((id) => completedSet.has(id), [completedSet]);

  return (
    <AcademyContext.Provider value={{
      progress, completedSet, overallPct, isDone,
      completeLesson, submitQuiz, lang, setLang, t,
    }}>
      {children}
    </AcademyContext.Provider>
  );
}

export const useAcademy = () => useContext(AcademyContext);
